import { memo, useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion, useTransform } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import { Interactive } from '@highlight/ui'
import { useActiveNotchToast } from '@renderer/overlay/Toaster/use-active-notch-toast'
import { useTrackNotchToastClosures } from '../../../track-notch'
import { useIsMiniRevealed } from '../../../atoms'
import { isMac } from '@shared/utils/platform'
import { MiniAnchorProvider } from '../side-context'
import { MiniDragHandle } from '../nested/drag-handle'
import { groupMiniNotchItems, type MiniNotchItem } from './notch-items'
import { NotchFillet, NOTCH_FILLET_SIZE } from './notch-fillet'
import { notchLayerVariants, notchResizeTransition, notchToastLayerVariants } from './notch-motion'
import { useMiniNotchRegion } from './notch-region'
import { layoutNotchRow, measureRowExtent, type NotchRowWidths } from './notch-row-layout'
import { NotchSlotList } from './notch-slot-list'
import {
  NotchToast,
  NOTCH_TOAST_RADIUS,
  NOTCH_TOAST_ROW_RADIUS,
  NOTCH_TOAST_ROW_WIDTH,
  NOTCH_TOAST_WIDTH,
} from './notch-toast'
import { useElementSize } from './use-element-size'
import { useNotchShape, type NotchShapeMode, type NotchShapeTarget } from './use-notch-shape'

const PHYSICAL_NOTCH_HEIGHT_BLEED = 1
const NOTCH_ROW_RADIUS = 12
/** Where the redesign's cutout fades to below its black top row. */
const NOTCH_GRADIENT_END = 'rgb(26 28 28)'

const dragHandleItem: MiniNotchItem = {
  id: 'drag-handle',
  slot: 'right',
  show: true,
  node: (
    <MiniAnchorProvider value={false}>
      <MiniDragHandle />
    </MiniAnchorProvider>
  ),
}

/** The redesign's titles and lists fit within this width; only the row's own controls may widen it past. */
export const MAX_REDESIGN_NOTCH_WIDTH = 440

interface MiniNotchProps {
  isCollapsed?: boolean
  items: Array<MiniNotchItem>
  onLogoClick: () => void
  /** A click on the notch itself, outside any of its controls. */
  onClick?: () => void
  /** The redesign's notch never narrows while this holds, so an ending meeting doesn't jitter as its row changes. */
  holdsWidth?: boolean
}

/**
 * Everything lives inside one black cutout. Its bounds are springs measured outward from the
 * physical notch center, and content sits in an anchor that counter-translates by the same value,
 * so content stays put while the cutout grows or clips around it. `below` items hang under the
 * bar and widen the same cutout.
 */
export const MiniNotch = memo(function MiniNotch({
  isCollapsed = false,
  items,
  onLogoClick,
  onClick,
  holdsWidth = false,
}: MiniNotchProps) {
  const region = useMiniNotchRegion()
  const isRevealed = useIsMiniRevealed()
  const activeToast = useActiveNotchToast()
  useTrackNotchToastClosures()
  const groups = useMemo(() => groupMiniNotchItems(items), [items])
  const rightItems = useMemo(
    () => (isRevealed && !isMac ? [...groups.right, dragHandleItem] : groups.right),
    [groups.right, isRevealed],
  )

  const [leftRef, leftSize] = useElementSize<HTMLDivElement>()
  const [centerRef, centerSize] = useElementSize<HTMLDivElement>()
  const [rightRef, rightSize] = useElementSize<HTMLDivElement>()
  const [belowRef, belowSize] = useElementSize<HTMLDivElement>()
  const [toastHeight, setToastHeight] = useState(0)

  const mode: NotchShapeMode = activeToast ? 'toast' : isCollapsed ? 'collapsed' : 'row'
  const showRow = mode === 'row'
  // A toast hangs from the top row, so hovering it must not change that row's height: the row would
  // move under the pointer, drop the hover, move back, and repeat.
  const isRowRevealed = isRevealed && mode !== 'toast'
  const restingRowHeight =
    (isRowRevealed ? region.height : region.restingHeight) +
    (region.hasNotch && mode !== 'collapsed' ? PHYSICAL_NOTCH_HEIGHT_BLEED : 0)
  const rowHeight = Math.max(restingRowHeight, showRow ? centerSize.height : 0)
  const showBelow = showRow && groups.below.length > 0
  const belowWidth = Math.max(belowSize.width, ...groups.below.map((item) => item.width ?? 0))
  const belowHalf = showBelow ? belowWidth / 2 : 0
  const rowRadius = Math.max(NOTCH_ROW_RADIUS, ...(showBelow ? groups.below.map((item) => item.cornerRadius ?? 0) : []))
  // With no physical notch in the way, center content joins the halves in one centered run
  const isCenteredRow = showRow && !region.hasNotch && groups.center.length > 0
  const rowWidths: NotchRowWidths = {
    left: showRow && groups.left.length > 0 ? leftSize.width : 0,
    center: centerSize.width,
    right: showRow && rightItems.length > 0 ? rightSize.width : 0,
    notch: region.width,
  }
  const rowExtent = measureRowExtent(rowWidths, isCenteredRow)
  const centeredExtent = Math.max(rowExtent, belowHalf)
  const [restingExtent, setRestingExtent] = useState(0)
  useEffect(() => {
    if (!holdsWidth) setRestingExtent(0)
    else if (showRow && !isRevealed) setRestingExtent((extent) => Math.max(extent, centeredExtent))
  }, [centeredExtent, holdsWidth, isRevealed, showRow])
  // The cap never clips the row's own items
  const extent = Math.max(
    Math.min(holdsWidth ? Math.max(restingExtent, centeredExtent) : centeredExtent, MAX_REDESIGN_NOTCH_WIDTH / 2),
    rowExtent,
  )
  const rowLayout = layoutNotchRow(rowWidths, extent, isCenteredRow)
  const belowHeight = showBelow ? belowSize.height : 0
  // A row toast takes the design's wide face: wider than the card, panel corners, and a shadow
  const isRowToast = Boolean(activeToast?.notchRow)
  const toastWidth = isRowToast ? NOTCH_TOAST_ROW_WIDTH : NOTCH_TOAST_WIDTH
  const toastRadius = isRowToast ? NOTCH_TOAST_ROW_RADIUS : NOTCH_TOAST_RADIUS
  const target = useMemo<NotchShapeTarget>(
    () =>
      mode === 'toast'
        ? {
            left: toastWidth / 2,
            right: toastWidth / 2,
            height: Math.max(toastHeight, rowHeight),
            radius: toastRadius,
          }
        : {
            left: extent,
            right: extent,
            height: rowHeight + belowHeight,
            radius: rowRadius,
          },
    [belowHeight, extent, mode, rowHeight, rowRadius, toastHeight, toastRadius, toastWidth],
  )

  const shape = useNotchShape(target, mode)
  const cutoutX = useTransform(() => -shape.left.get())
  const leftFilletX = useTransform(() => -shape.left.get() - NOTCH_FILLET_SIZE)
  // A collapsed cutout tucks behind a physical notch, but the stand-in stays on screen and keeps its corners
  const showFillets = mode !== 'collapsed' || !region.hasNotch

  return (
    <>
      <NotchFillet side="left" x={leftFilletX} isVisible={showFillets} />
      <NotchFillet side="right" x={shape.right} isVisible={showFillets} />

      <motion.div
        // A clickable notch takes the pointer across its whole row, and its items can brighten on that hover
        className={twMerge(
          'pointer-events-auto absolute left-0 top-0 overflow-hidden bg-black transition-shadow duration-300',
          // The design's own pair, without a shadow step's edge: a hairline would ring the hardware black
          isRowToast && 'shadow-[0_8px_16px_0_var(--black-4),0_24px_48px_-4px_var(--black-6)]',
          onClick && showRow && 'group/notch cursor-pointer',
        )}
        style={{
          x: cutoutX,
          width: shape.width,
          height: shape.height,
          borderBottomLeftRadius: shape.radius,
          borderBottomRightRadius: shape.radius,
          // Black through the row, which must match the physical notch, then fading over what hangs below it
          background: `linear-gradient(to bottom, black ${rowHeight}px, ${NOTCH_GRADIENT_END})`,
        }}
        // A toast shares the cutout, so only a click on the row itself counts
        onClick={
          onClick &&
          ((event) => {
            if (showRow && !(event.target as Element).closest('button, a, input, textarea, [data-notch-below]')) {
              onClick()
            }
          })
        }
      >
        <Interactive />

        <motion.div className="absolute left-0 top-0" style={{ x: shape.left }}>
          <motion.div
            className={twMerge('absolute left-0 top-0', !showRow && 'pointer-events-none')}
            variants={notchLayerVariants}
            initial={false}
            animate={showRow ? 'visible' : 'hidden'}
            aria-hidden={!showRow}
          >
            <motion.div
              className="relative"
              initial={false}
              animate={{ height: rowHeight }}
              transition={notchResizeTransition}
            >
              <motion.div
                ref={leftRef}
                className="absolute inset-y-0 left-0 flex w-max items-center pl-1"
                initial={false}
                animate={{ x: rowLayout.leftStart }}
                transition={notchResizeTransition}
              >
                <MiniAnchorProvider value={false}>
                  <NotchSlotList slot="left" items={groups.left} />
                </MiniAnchorProvider>
              </motion.div>

              {/* Clips the center to its springing width so it never paints under the halves */}
              <motion.div
                className="absolute inset-y-0 left-0 flex items-center justify-center overflow-hidden"
                initial={false}
                animate={rowLayout.center}
                transition={notchResizeTransition}
              >
                <div
                  ref={centerRef}
                  className="flex w-max shrink-0 items-center"
                  style={isCenteredRow ? undefined : { minWidth: region.width }}
                >
                  <MiniAnchorProvider value={true}>
                    <NotchSlotList slot="center" items={groups.center} />
                  </MiniAnchorProvider>
                </div>
              </motion.div>

              <motion.div
                ref={rightRef}
                className="absolute inset-y-0 left-0 flex w-max items-center pr-1"
                initial={false}
                animate={{ x: rowLayout.rightStart }}
                transition={notchResizeTransition}
              >
                <MiniAnchorProvider value={true}>
                  <NotchSlotList slot="right" items={rightItems} />
                </MiniAnchorProvider>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Always mounted: its list's `initial={false}` would otherwise skip every entrance below the row */}
          <div
            ref={belowRef}
            data-notch-below
            // Only the row is the notch's click target; what hangs below it keeps its own controls
            className="absolute left-0 w-max -translate-x-1/2 cursor-default"
            // Centered, the row spans the whole cutout and is held to that width
            style={{ top: rowHeight, width: extent * 2, overflow: 'hidden' }}
          >
            <MiniAnchorProvider value={true}>
              <NotchSlotList slot="below" items={showBelow ? groups.below : []} />
            </MiniAnchorProvider>
          </div>

          <AnimatePresence>
            {activeToast && (
              <motion.div
                key="notch-toast"
                className="absolute top-0 w-max"
                style={{ left: -toastWidth / 2 }}
                variants={notchToastLayerVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
              >
                <NotchToast
                  toast={activeToast}
                  topInset={rowHeight}
                  onHeightChange={setToastHeight}
                  onLogoClick={onLogoClick}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </>
  )
})

export type { MiniNotchItem, MiniNotchSlot } from './notch-items'
